import { useLocation } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import qs from 'qs';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';

import { Loading } from 'components/Loading';
import { useModel } from 'hooks/useModel';
import { useStore } from 'models/Provider';

import { GetAcceptSettings } from './Models';

export const GetAcceptSettingsPage = observer(() => {
  const { t, api } = useStore();
  const { showError, showSuccess } = useToaster();
  const location = useLocation();
  const model = useModel(GetAcceptSettings, {}, { api, showError, showSuccess, t });
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });
  const code = parsed['code'];
  const redirectUrl = `${window.location.origin}/settings/apps/getaccept/`;
  if (code && !model.code) {
    model.setCode(redirectUrl, code);
  }

  return (
    <Stack space={6}>
      <Text font="heading2">{t('integrations.getaccept.settings.header')}</Text>
      <Text font="heading2">{t('integrations.getaccept.settings.header.settings')}</Text>
      <Stack space={3}>
        <Text>{t('integrations.getaccept.settings.instructions.settings')}</Text>
        <Checkbox
          value={model.needsApproval}
          label={t('integrations.getaccept.settings.needsapproval.label')}
          onChange={model.toggleNeedsApproval}
        />
        <TextField
          label={t('integrations.getaccept.settings.needsapprovallabel.label')}
          value={model.needsApprovalLabel || ''}
          onChange={model.setNeedsApprovalLabel}
          error={
            model.needsApprovalError ? t('integrations.getaccept.settings.needsapproval.error') : ''
          }
        />
        <div>
          <Button
            isDisabled={model.submitting}
            onClick={model.saveSettings}
            text={t('integrations.getaccept.settings.save')}
          />
        </div>
      </Stack>
      <Separator />
      <Text font="heading2">{t('integrations.getaccept.settings.header.templates')}</Text>
      {!model.isAuthed && !code && (
        <Stack space={4} align="flex-start">
          <Text>{t('integrations.getaccept.settings.noauth')}</Text>
          <Button
            as="a"
            href={`https://app.getaccept.com/oauth2/authorize?response_type=code&client_id=api&redirect_uri=${redirectUrl}&scope=basic`}
            text={t('integrations.getaccept.settings.auth.button')}
          />
        </Stack>
      )}
      {code && <Text>{t('integrations.getaccept.settings.authed')}</Text>}
      {(model.loadingTemplates || model.checkingAuth || model.submitting) && <Loading />}
      {model.isAuthed && !model.loadingTemplates && model.templates.length === 0 && (
        <Text text={t('integrations.getaccept.settings.instructions.notemplate')} />
      )}
      {model.isAuthed && !model.loadingTemplates && model.templates.length > 0 && (
        <Stack space={6}>
          <Select
            label={t('integrations.getaccept.settings.instructions.templates')}
            options={model.templates.map((template) => {
              return { value: template.id, label: template.name };
            })}
            onChange={model.setTemplateId}
          />
          {model.loadingFields && <Loading />}
          {!model.loadingFields && model.loadedFields && (
            <Stack space={4}>
              <Stack space={2}>
                <Text font="bold16">{t('integrations.getaccept.settings.subheader')}</Text>
                {model.fields.length > 0 && (
                  <Text>{t('integrations.getaccept.settings.fields.instructions')}</Text>
                )}
                {model.fields.length === 0 && (
                  <Text>{t('integrations.getaccept.settings.fields.instructions.nofields')}</Text>
                )}
              </Stack>
              {model.fields.map((field) => {
                return (
                  <Select
                    options={field.initialValue.map((value) => {
                      return { value: value[0], label: value[1] };
                    })}
                    label={field.label}
                    onChange={field.setValue}
                    defaultValue={field.value}
                    key={field.name}
                  />
                );
              })}
            </Stack>
          )}
          {model.loadedFields && (
            <Stack space={0} orientation="horizontal" justify="flex-end">
              <Button
                disabled={model.submitting}
                onClick={model.submitTemplate}
                text={t('integrations.getaccept.settings.templates.submit')}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
});
